import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../../services/api';
import {
  MESSAGE_TEMPLATES,
  MESSAGE_CHANNELS,
  PLACEHOLDER_HINTS,
} from './messageConstants';
import {
  createTenantNormalizer,
  getSmsParts,
  buildSendSummary,
  getEligibleChannelsForTenant,
  canSendToTenant,
  fillTemplate,
  buildWhatsAppMessage,
  buildWhatsAppUrl,
} from './messageUtils';
import SendConfirmDialog from './SendConfirmDialog';
import './Messages.css';

const TEMPLATE_OPTIONS = Object.values(MESSAGE_TEMPLATES);

const defaultChannels = {
  email: true,
  sms: false,
  whatsapp: false,
};

export default function SendMessages({ user, selectedContractId, projectName, onOpenSentHistory }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [templateKey, setTemplateKey] = useState('');
  const [channels, setChannels] = useState(defaultChannels);
  const [tenantSearch, setTenantSearch] = useState('');
  const [onlyWithConsent, setOnlyWithConsent] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSummary, setConfirmSummary] = useState(null);
  const [sending, setSending] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  useEffect(() => {
    if (!selectedContractId || !user) return;

    let cancelled = false;

    const fetchTenants = async () => {
      try {
        setLoading(true);
        setError(null);
        setSelectedIds(new Set());

        const response = await api.getTenantsReport(
          user.userCode,
          user.type,
          selectedContractId
        );

        if (cancelled) return;

        const columns = response.columns || [];
        const normalize = createTenantNormalizer(columns);
        const rows = (response.data || []).map((row, index) => normalize(row, index));
        setTenants(rows);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading tenants for messaging:', err);
          setError(err.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTenants();
    return () => { cancelled = true; };
  }, [selectedContractId, user]);

  const smsInfo = useMemo(() => getSmsParts(body), [body]);

  const templateContext = useMemo(() => ({
    projectName,
    contractorName: user?.username || '',
  }), [projectName, user?.username]);

  const getTenantWhatsAppUrl = useCallback((tenant) => {
    const message = buildWhatsAppMessage(subject, body, tenant, templateContext);
    return buildWhatsAppUrl(tenant.phone, message);
  }, [subject, body, templateContext]);

  const filteredTenants = useMemo(() => {
    const q = tenantSearch.trim().toLowerCase();
    return tenants.filter((tenant) => {
      if (onlyWithConsent) {
        const hasAnyConsent = Object.keys(MESSAGE_CHANNELS).some((key) =>
          channels[key] ? canSendToTenant(tenant, key) : false
        );
        if (!hasAnyConsent) return false;
      }

      if (!q) return true;

      const haystack = [
        tenant.name,
        tenant.apartment,
        tenant.building,
        tenant.appType,
        tenant.phone,
        tenant.email,
        tenant.city,
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [tenants, tenantSearch, onlyWithConsent, channels]);

  const selectedTenants = useMemo(
    () => tenants.filter((t) => selectedIds.has(t.id)),
    [tenants, selectedIds]
  );

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allFilteredSelected = filteredTenants.every((t) => prev.has(t.id));
      if (allFilteredSelected) {
        const next = new Set(prev);
        filteredTenants.forEach((t) => next.delete(t.id));
        return next;
      }
      const next = new Set(prev);
      filteredTenants.forEach((t) => next.add(t.id));
      return next;
    });
  }, [filteredTenants]);

  const toggleTenant = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleTemplateChange = (key) => {
    setTemplateKey(key);
    if (!key) return;

    const template = MESSAGE_TEMPLATES[key];
    if (!template) return;

    const applyTemplate = () => {
      setSubject(template.subject);
      setBody(template.body);
    };

    if (subject.trim() || body.trim()) {
      if (window.confirm('להחליף את תוכן ההודעה בתבנית שנבחרה?')) {
        applyTemplate();
      }
    } else {
      applyTemplate();
    }
  };

  const insertPlaceholder = (token) => {
    setBody((prev) => (prev ? `${prev} ${token}` : token));
  };

  const handleChannelChange = (key, checked) => {
    setChannels((prev) => ({ ...prev, [key]: checked }));
  };

  const handleSendClick = () => {
    if (!subject.trim()) {
      setStatusMessage({ type: 'error', text: 'נא להזין כותרת להודעה' });
      return;
    }
    if (!body.trim()) {
      setStatusMessage({ type: 'error', text: 'נא להזין תוכן להודעה' });
      return;
    }
    if (selectedTenants.length === 0) {
      setStatusMessage({ type: 'error', text: 'נא לבחור לפחות דייר אחד' });
      return;
    }

    const activeChannels = Object.keys(channels).filter((k) => channels[k]);
    if (activeChannels.length === 0) {
      setStatusMessage({ type: 'error', text: 'נא לבחור לפחות ערוץ שליחה אחד' });
      return;
    }

    const summary = buildSendSummary(selectedTenants, channels, body);
    setConfirmSummary(summary);
    setConfirmOpen(true);
    setStatusMessage(null);
  };

  const handleConfirmSend = async () => {
    setConfirmOpen(false);
    setSending(true);
    setStatusMessage(null);

    const templateContext = {
      projectName,
      contractorName: user?.username || '',
    };

    const recipients = selectedTenants
      .map((tenant) => ({
        tenantId: tenant.id,
        channels: getEligibleChannelsForTenant(tenant, channels),
        subject: fillTemplate(subject, tenant, templateContext),
        body: fillTemplate(body, tenant, templateContext),
      }))
      .filter((r) => r.channels.length > 0);

    try {
      await api.sendMessages({
        userCode: user.userCode,
        userType: user.type,
        contractId: selectedContractId,
        templateType: templateKey || null,
        subject,
        body,
        channels: Object.keys(channels).filter((k) => channels[k]),
        recipients,
      });

      setStatusMessage({
        type: 'success',
        text: `ההודעה נשלחה בהצלחה ל-${recipients.length} דיירים`,
      });
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Send messages error:', err);
      setStatusMessage({
        type: 'error',
        text: err.message || 'שגיאה בשליחת ההודעה',
      });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="report-panel report-panel--state">
        <div className="report-state-message">טוען דיירים...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="report-panel report-panel--state">
        <div className="report-state-message report-state-message--error">
          שגיאה בטעינת דיירים: {error}
        </div>
      </div>
    );
  }

  const allFilteredSelected =
    filteredTenants.length > 0 && filteredTenants.every((t) => selectedIds.has(t.id));

  return (
    <div className="messages-page report-panel report-panel--messages">
      <div className="messages-page-scroll">
        {statusMessage && (
          <div className={`messages-status messages-status--${statusMessage.type}`}>
            {statusMessage.text}
          </div>
        )}

        <section className="messages-compose">
          <div className="messages-compose-header">
            <h2 className="messages-compose-title">שליחת הודעות לדיירים</h2>
            <div className="messages-compose-toolbar">
              <label className="messages-template-label" htmlFor="msg-template">תבנית</label>
              <select
                id="msg-template"
                className="messages-template-select"
                value={templateKey}
                onChange={(e) => handleTemplateChange(e.target.value)}
              >
                <option value="">בחר תבנית...</option>
                {TEMPLATE_OPTIONS.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
              {onOpenSentHistory && (
                <button
                  type="button"
                  className="messages-btn messages-btn--history messages-history-btn"
                  onClick={onOpenSentHistory}
                >
                  דוח היסטוריית הודעות
                </button>
              )}
            </div>
          </div>

          <div className="messages-subject-row">
            <div className="messages-field messages-field--inline messages-subject-field">
              <label className="messages-field-label" htmlFor="msg-subject">כותרת</label>
              <input
                id="msg-subject"
                className="messages-input"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="נושא ההודעה"
              />
            </div>

            <div className="messages-channels-group">
              <span className="messages-field-label">ערוצים</span>
              <div className="messages-channels messages-channels--inline">
                {Object.values(MESSAGE_CHANNELS).map((ch) => (
                  <label key={ch.key} className="messages-channel-check">
                    <input
                      type="checkbox"
                      checked={channels[ch.key]}
                      onChange={(e) => handleChannelChange(ch.key, e.target.checked)}
                    />
                    {ch.label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="messages-body-row">
            <div className="messages-body-main">
              <label className="messages-field-label" htmlFor="msg-body">תוכן ההודעה</label>
              <textarea
                id="msg-body"
                className="messages-textarea"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="כתוב את תוכן ההודעה..."
              />
              <div className={`messages-sms-counter${smsInfo.parts > 1 ? ' messages-sms-counter--warn' : ''}`}>
                <span>תווים: {smsInfo.chars}{smsInfo.limit ? ` / ${smsInfo.limit}` : ''}</span>
                {channels.sms && smsInfo.chars > 0 && (
                  <span>SMS: {smsInfo.costLabel}</span>
                )}
                {smsInfo.isUnicode && smsInfo.chars > 0 && (
                  <span>עברית = Unicode</span>
                )}
              </div>
            </div>

            <aside className="messages-placeholders-side">
              <span className="messages-placeholders-title">משתנים</span>
              <div className="messages-placeholders">
                {PLACEHOLDER_HINTS.map((p) => (
                  <button
                    key={p.token}
                    type="button"
                    className="messages-placeholder-btn"
                    onClick={() => insertPlaceholder(p.token)}
                    title={`הוסף ${p.label}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </aside>
          </div>
      </section>

      {selectedTenants.length > 0 && (
        <section className="messages-selected-panel">
          <h3 className="messages-selected-title">
            נמענים נבחרים ({selectedTenants.length})
          </h3>
          <div className="messages-selected-chips">
            {selectedTenants.map((tenant) => (
              <span key={tenant.id} className="messages-chip">
                {tenant.displayLabel}
                <button
                  type="button"
                  className="messages-chip-remove"
                  onClick={() => toggleTenant(tenant.id)}
                  aria-label={`הסר ${tenant.displayLabel}`}
                  title="הסר מרשימת הנמענים"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </section>
      )}

      <section className="messages-tenants-section">
        <div className="messages-tenants-toolbar">
          <span>
            <strong>{tenants.length}</strong> דיירים · סמני את מי לשלוח
            {selectedIds.size > 0 && <> · נבחרו <strong>{selectedIds.size}</strong></>}
          </span>
          <div className="messages-tenants-toolbar-end">
            <label className="messages-filter-check">
              <input
                type="checkbox"
                checked={onlyWithConsent}
                onChange={(e) => setOnlyWithConsent(e.target.checked)}
              />
              רק מי שאישר קבלת הודעות
            </label>
            <input
              type="search"
              className="messages-search"
              placeholder="חיפוש דייר..."
              value={tenantSearch}
              onChange={(e) => setTenantSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="messages-tenants-table-wrap">
          <table className="messages-tenants-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAll}
                    aria-label="בחר הכל"
                  />
                </th>
                <th>דירה</th>
                <th>בניין</th>
                <th>טיפוס</th>
                <th>שם</th>
                <th>טלפון</th>
                <th>מייל</th>
                <th>עיר</th>
                <th title="אישור מייל">מייל ✓</th>
                <th title="אישור SMS">SMS ✓</th>
                <th title="אישור וואצאפ">WA ✓</th>
                <th>WhatsApp</th>
              </tr>
            </thead>
            <tbody>
              {filteredTenants.length === 0 ? (
                <tr>
                  <td colSpan={12} style={{ textAlign: 'center', padding: '24px' }}>
                    לא נמצאו דיירים
                  </td>
                </tr>
              ) : (
                filteredTenants.map((tenant) => {
                  const eligible = getEligibleChannelsForTenant(tenant, channels);
                  const isBlocked = channels.email || channels.sms || channels.whatsapp
                    ? eligible.length === 0
                    : false;
                  const whatsAppUrl = getTenantWhatsAppUrl(tenant);

                  return (
                    <tr
                      key={tenant.id}
                      className={[
                        isBlocked ? 'messages-row--blocked' : '',
                        selectedIds.has(tenant.id) ? 'messages-row--selected' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => toggleTenant(tenant.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(tenant.id)}
                          onChange={() => toggleTenant(tenant.id)}
                          aria-label={`בחר ${tenant.displayLabel}`}
                        />
                      </td>
                      <td>{tenant.apartment || '—'}</td>
                      <td>{tenant.building || '—'}</td>
                      <td>{tenant.appType || '—'}</td>
                      <td>{tenant.name || '—'}</td>
                      <td>{tenant.phone || '—'}</td>
                      <td>{tenant.email || '—'}</td>
                      <td>{tenant.city || '—'}</td>
                      <td>
                        <span className={tenant.AlwEmail ? 'messages-consent-yes' : 'messages-consent-no'}>
                          {tenant.AlwEmail ? 'כן' : 'לא'}
                        </span>
                      </td>
                      <td>
                        <span className={tenant.U_AlwSms ? 'messages-consent-yes' : 'messages-consent-no'}>
                          {tenant.U_AlwSms ? 'כן' : 'לא'}
                        </span>
                      </td>
                      <td>
                        <span className={tenant.U_AlwWApp ? 'messages-consent-yes' : 'messages-consent-no'}>
                          {tenant.U_AlwWApp ? 'כן' : 'לא'}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {whatsAppUrl ? (
                          <a
                            href={whatsAppUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="messages-wa-link"
                            title="פתיחת הודעה ב-WhatsApp"
                            aria-label={`פתח WhatsApp עבור ${tenant.displayLabel}`}
                          >
                            פתח
                          </a>
                        ) : (
                          <span
                            className="messages-wa-link messages-wa-link--disabled"
                            title={tenant.phone ? 'הזן כותרת או תוכן להודעה' : 'אין מספר טלפון'}
                          >
                            —
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
      </div>

      <div className="messages-sticky-bar">
        <div className="messages-sticky-summary">
          {selectedIds.size > 0 ? (
            <>
              <strong>שליחה ל-{selectedIds.size} דיירים</strong>
              <span>
                {selectedTenants.map((t) => t.displayLabel).slice(0, 3).join(' | ')}
                {selectedTenants.length > 3 && ` | +${selectedTenants.length - 3} נוספים`}
              </span>
            </>
          ) : (
            <>
              <strong>לא נבחרו נמענים</strong>
              <span className="messages-sticky-hint">סמני דיירים בטבלה למעלה</span>
            </>
          )}
        </div>
        <div className="messages-sticky-actions">
          <button
            type="button"
            className="messages-btn messages-btn--primary"
            onClick={handleSendClick}
            disabled={sending}
          >
            {sending ? 'שולח...' : 'שלח הודעה'}
          </button>
        </div>
      </div>

      <SendConfirmDialog
        open={confirmOpen}
        summary={confirmSummary}
        onConfirm={handleConfirmSend}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
